"use client";

import { AlertTriangle, Activity, RadioTower } from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { useGlobeFrameStream } from "@/hooks/use-globe-frame-stream";
import { GLOBE_VIEW_CONFIG } from "@/lib/data/globe-view-config";
import { BASELINE_GLOBE_RENDER_SETTINGS, type GlobeRenderSettings } from "@/lib/data/globe-render-settings";
import {
  COUNTRY_BOUNDARY_SOURCE_URL,
  extractBoundaryLineStrings,
  splitAntimeridianLine,
  type BoundaryCoordinate,
  type BoundaryFeatureCollection,
} from "@/lib/globe-boundaries";
import {
  DEFAULT_GDELT_MOCK_FRAME_URL,
  HOT_ZONE_CATEGORY_STYLE,
  getHotZonePulseSpeed,
  getHotZoneVisualWeight,
  latLonToCartesian,
} from "@/lib/gdelt-globe";
import { getEventHoverHaloScale } from "@/lib/globe-event-focus";
import { getGlobeRenderProfile, type GlobeRenderProfile } from "@/lib/globe-render-profile";
import { cn } from "@/lib/utils";
import { findObjObject, getObjBoundaryLines, parseObjGlobeModel, type ObjGlobeModel, type ObjVector } from "@/lib/obj-globe-model";
import type { GlobeCameraUrlState } from "@/lib/gdelt-globe";
import type { GlobeFrame, GlobeMapMode, GlobeStreamSource, HotZone, HotZoneCategory } from "@/types/gdelt";

type AnarchyGlobeProps = {
  initialFrame?: GlobeFrame;
  frame?: GlobeFrame;
  streamSource?: GlobeStreamSource;
  mapMode?: GlobeMapMode;
  selectedZoneId?: string | null;
  onZoneSelect?: (zone: HotZone) => void;
  onZoneHover?: (zone: HotZone | null) => void;
  hoverDelayMs?: number;
  initialCameraState?: GlobeCameraUrlState | null;
  onCameraStateChange?: (state: GlobeCameraUrlState) => void;
  showTelemetryOverlay?: boolean;
  cameraFov?: number;
  cameraLowerRadiusLimit?: number;
  markerStyle?: "pressure" | "export";
  showProceduralLandShapes?: boolean;
  renderSettings?: GlobeRenderSettings;
  modelSourceUrl?: string;
  infoCardModelSourceUrl?: string;
  eventOverlay?: ReactNode;
  eventPanelHtmlRef?: RefObject<HTMLElement | null>;
  eventPanelHtmlContent?: ReactNode;
  eventPanelHtmlSize?: Readonly<{ width: number; height: number }>;
  boundarySourceUrl?: string;
  className?: string;
};

type BabylonModule = typeof import("@babylonjs/core");

type SceneRefs = {
  engine: import("@babylonjs/core").Engine;
  scene: import("@babylonjs/core").Scene;
  camera: import("@babylonjs/core").ArcRotateCamera;
  earth: import("@babylonjs/core").Mesh;
  markerMeshes: Map<HotZoneCategory, import("@babylonjs/core").Mesh>;
  markerStyle: "pressure" | "export";
  renderSettings: GlobeRenderSettings;
  usesObjModel: boolean;
  objModel: ObjGlobeModel | null;
  hoverHalo: import("@babylonjs/core").Mesh;
  eventCardRoot: import("@babylonjs/core").TransformNode | null;
  eventCardHtmlMesh: import("@babylonjs/addons").HtmlMesh | null;
  htmlMeshRenderer: import("@babylonjs/addons").HtmlMeshRenderer | null;
  zoneLookup: Map<HotZoneCategory, HotZone[]>;
  countryBoundaryMeshes: import("@babylonjs/core").LinesMesh[];
  outlineMeshes: import("@babylonjs/core").LinesMesh[];
  resizeObserver: ResizeObserver;
  beforeRenderObserver: import("@babylonjs/core").Nullable<import("@babylonjs/core").Observer<import("@babylonjs/core").Scene>>;
};

const CATEGORY_ORDER = Object.keys(HOT_ZONE_CATEGORY_STYLE) as HotZoneCategory[];
const EARTH_RADIUS = GLOBE_VIEW_CONFIG.globe.earthRadius;
const MARKER_RADIUS = EARTH_RADIUS + GLOBE_VIEW_CONFIG.globe.markerRadiusOffset;
const OBJ_MODEL_CACHE = new Map<string, Promise<ObjGlobeModel>>();
const DEFAULT_EVENT_PANEL_HTML_SIZE = Object.freeze({ width: 4, height: 2.25 });

export function AnarchyGlobe({
  initialFrame,
  frame: controlledFrame,
  streamSource,
  mapMode = "outline",
  selectedZoneId: controlledSelectedZoneId,
  onZoneSelect,
  onZoneHover,
  hoverDelayMs = 80,
  initialCameraState,
  onCameraStateChange,
  showTelemetryOverlay = true,
  cameraFov = GLOBE_VIEW_CONFIG.camera.fov,
  cameraLowerRadiusLimit = GLOBE_VIEW_CONFIG.camera.lowerRadiusLimit,
  markerStyle = "pressure",
  showProceduralLandShapes = true,
  renderSettings = BASELINE_GLOBE_RENDER_SETTINGS,
  modelSourceUrl,
  infoCardModelSourceUrl,
  eventOverlay,
  eventPanelHtmlRef,
  eventPanelHtmlContent,
  eventPanelHtmlSize = DEFAULT_EVENT_PANEL_HTML_SIZE,
  boundarySourceUrl = COUNTRY_BOUNDARY_SOURCE_URL,
  className,
}: AnarchyGlobeProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const htmlMeshContainerId = `anarchy-globe-html-${useId().replace(/:/g, "")}`;
  const sceneRef = useRef<SceneRefs | null>(null);
  const frameRef = useRef<GlobeFrame | null>(initialFrame ?? null);
  const mapModeRef = useRef(mapMode);
  const selectedZoneIdRef = useRef<string | null>(controlledSelectedZoneId ?? null);
  const onZoneSelectRef = useRef(onZoneSelect);
  const onZoneHoverRef = useRef(onZoneHover);
  const onCameraStateChangeRef = useRef(onCameraStateChange);
  const initialCameraStateRef = useRef(initialCameraState);
  const cameraFovRef = useRef(cameraFov);
  const cameraLowerRadiusLimitRef = useRef(cameraLowerRadiusLimit);
  const selectionControlledRef = useRef(controlledSelectedZoneId !== undefined);
  const lastFlightZoneIdRef = useRef<string | null>(null);
  const lastCameraStateEmissionRef = useRef({ at: 0, signature: "" });
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoveredZoneRef = useRef<HotZone | null>(null);
  const hoverLabelRef = useRef<HTMLDivElement | null>(null);
  const cardAnimationStartedAtRef = useRef(0);
  const [ready, setReady] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [renderAttempt, setRenderAttempt] = useState(0);
  const [internalSelectedZoneId, setInternalSelectedZoneId] = useState<string | null>(null);
  const [hoveredWord, setHoveredWord] = useState<string | null>(null);
  const fallbackSource = useMemo<GlobeStreamSource>(
    () => streamSource ?? { mode: "mock", url: DEFAULT_GDELT_MOCK_FRAME_URL },
    [streamSource],
  );
  const stream = useGlobeFrameStream(fallbackSource, initialFrame, { enabled: !controlledFrame });
  const frame = controlledFrame ?? stream.frame;
  const status = controlledFrame ? "open" : stream.status;
  const error = controlledFrame ? null : stream.error;
  const selectedZoneId = controlledSelectedZoneId ?? internalSelectedZoneId;
  const categoryStyle = Object.fromEntries(
    CATEGORY_ORDER.map((category) => [category, { ...HOT_ZONE_CATEGORY_STYLE[category], ...renderSettings.signals[category] }]),
  ) as typeof HOT_ZONE_CATEGORY_STYLE;

  frameRef.current = frame;
  mapModeRef.current = mapMode;
  selectedZoneIdRef.current = selectedZoneId;
  onZoneSelectRef.current = onZoneSelect;
  onZoneHoverRef.current = onZoneHover;
  onCameraStateChangeRef.current = onCameraStateChange;
  selectionControlledRef.current = controlledSelectedZoneId !== undefined;
  cameraFovRef.current = cameraFov;
  cameraLowerRadiusLimitRef.current = cameraLowerRadiusLimit;

  const focusHoveredZone = useCallback((zone: HotZone | null) => {
    hoveredZoneRef.current = zone;
    setHoveredWord(zone ? getZoneSignificantWord(zone) : null);
    onZoneHoverRef.current?.(zone);
  }, []);

  const selectedZone = useMemo(() => {
    if (!frame?.zones.length) return null;
    return frame.zones.find((zone) => zone.zone_id === selectedZoneId) ?? null;
  }, [frame, selectedZoneId]);

  const selectZone = useCallback((zone: HotZone, shouldFly = true) => {
    if (!selectionControlledRef.current) {
      setInternalSelectedZoneId(zone.zone_id);
    }
    onZoneSelectRef.current?.(zone);

    const refs = sceneRef.current;
    if (!refs || !shouldFly) return;

    lastFlightZoneIdRef.current = zone.zone_id;
    void import("@babylonjs/core").then((B) => {
      flyCameraToZone(B, refs.scene, refs.camera, zone);
      renderHotZoneInstances(B, refs.markerMeshes, frameRef.current?.zones ?? [], performance.now() / 1000, zone.zone_id, refs.zoneLookup, refs.markerStyle, refs.renderSettings);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    let initializingEngine: import("@babylonjs/core").Engine | null = null;

    async function createScene() {
      if (!canvasRef.current || !containerRef.current) return;

      setReady(false);
      setRenderError(null);
      const renderProfile = getBrowserGlobeRenderProfile(containerRef.current);

      const [B, addons, objModel, infoCardModel] = await Promise.all([
        import("@babylonjs/core"),
        eventPanelHtmlRef?.current ? import("@babylonjs/addons") : Promise.resolve(null),
        modelSourceUrl && renderProfile.loadObjGlobeModel
          ? loadObjGlobeModel(modelSourceUrl).catch(() => null)
          : Promise.resolve(null),
        infoCardModelSourceUrl ? loadObjGlobeModel(infoCardModelSourceUrl).catch(() => null) : Promise.resolve(null),
      ]);
      if (cancelled || !canvasRef.current || !containerRef.current) return;
      if (!B.Engine.isSupported()) throw new Error("WebGL is unavailable in this browser");

      const engine = new B.Engine(canvasRef.current, renderProfile.antialias, {
        antialias: renderProfile.antialias,
        preserveDrawingBuffer: false,
        stencil: renderProfile.stencil,
        failIfMajorPerformanceCaveat: false,
        powerPreference: "default",
      });
      initializingEngine = engine;
      engine.setHardwareScalingLevel(renderProfile.hardwareScalingLevel);
      const scene = new B.Scene(engine);
      scene.clearColor = new B.Color4(...renderSettings.sceneClear);
      scene.skipPointerMovePicking = false;
      const initialCamera = initialCameraStateRef.current;

      const camera = new B.ArcRotateCamera(
        "anarchy-globe-camera",
        initialCamera?.alpha ?? GLOBE_VIEW_CONFIG.camera.alpha,
        initialCamera?.beta ?? GLOBE_VIEW_CONFIG.camera.beta,
        initialCamera?.radius ?? GLOBE_VIEW_CONFIG.camera.radius,
        B.Vector3.Zero(),
        scene,
      );
      camera.target = new B.Vector3(
        initialCamera?.targetX ?? 0,
        initialCamera?.targetY ?? 0,
        initialCamera?.targetZ ?? 0,
      );
      camera.fov = cameraFovRef.current;
      camera.lowerRadiusLimit = cameraLowerRadiusLimitRef.current;
      camera.upperRadiusLimit = GLOBE_VIEW_CONFIG.camera.upperRadiusLimit;
      camera.wheelPrecision = GLOBE_VIEW_CONFIG.camera.wheelPrecision;
      camera.pinchPrecision = GLOBE_VIEW_CONFIG.camera.pinchPrecision;
      camera.inertia = GLOBE_VIEW_CONFIG.camera.inertia;
      camera.angularSensibilityX = GLOBE_VIEW_CONFIG.camera.angularSensibilityX;
      camera.angularSensibilityY = GLOBE_VIEW_CONFIG.camera.angularSensibilityY;
      camera.attachControl(canvasRef.current, true);

      const glow = new B.GlowLayer("export-signal-glow", scene, {
        blurKernelSize: renderProfile.glowBlurKernelSize,
        mainTextureFixedSize: renderProfile.glowTextureSize,
      });
      glow.intensity = renderSettings.signalGlowIntensity;

      const hemispheric = new B.HemisphericLight("hemispheric-blue", new B.Vector3(0.2, 1, 0.3), scene);
      hemispheric.intensity = renderSettings.lights.hemisphereIntensity;
      hemispheric.diffuse = new B.Color3(...renderSettings.lights.hemisphereColor);
      hemispheric.groundColor = new B.Color3(...renderSettings.lights.hemisphereGroundColor);

      const key = new B.DirectionalLight("solar-key", new B.Vector3(-1.3, -0.6, -1), scene);
      key.position = new B.Vector3(3, 2, 4);
      key.intensity = renderSettings.lights.keyIntensity;
      key.diffuse = new B.Color3(...renderSettings.lights.keyColor);

      const rim = new B.PointLight("atmospheric-rim", new B.Vector3(-2.7, 1.4, -2.4), scene);
      rim.intensity = renderSettings.lights.rimIntensity;
      rim.diffuse = new B.Color3(...renderSettings.lights.rimColor);

      const earth = objModel
        ? createObjSurfaceMesh(B, scene, objModel, "AnarchyGlobe_Core", "earth-core")
        : B.MeshBuilder.CreateSphere(
            "earth-core",
            { diameter: EARTH_RADIUS * 2, segments: renderProfile.earthSegments },
            scene,
          );
      applyEarthMaterial(B, scene, earth, mapModeRef.current, showProceduralLandShapes, renderSettings);

      const atmosphere = objModel
        ? createObjSurfaceMesh(B, scene, objModel, "AtmosphereShell", "atmosphere-shell")
        : B.MeshBuilder.CreateSphere(
            "atmosphere-shell",
            { diameter: EARTH_RADIUS * 2 * GLOBE_VIEW_CONFIG.globe.atmosphereScale, segments: renderProfile.atmosphereSegments },
            scene,
          );
      const atmosphereMaterial = new B.StandardMaterial("atmosphere-glow", scene);
      atmosphereMaterial.emissiveColor = new B.Color3(...renderSettings.atmosphere.emissive);
      atmosphereMaterial.diffuseColor = new B.Color3(...renderSettings.atmosphere.diffuse);
      atmosphereMaterial.alpha = renderSettings.atmosphere.alpha;
      atmosphereMaterial.backFaceCulling = false;
      atmosphereMaterial.disableLighting = true;
      atmosphere.material = atmosphereMaterial;

      const objBoundaryMeshes = objModel ? createObjBoundaryMeshes(B, scene, objModel, renderSettings) : [];
      const outlineMeshes = objModel
        ? createObjGridMeshes(B, scene, objModel, mapModeRef.current, renderSettings)
        : createOutlineMeshes(B, scene, mapModeRef.current, renderSettings);
      const markerMeshes = createMarkerMeshes(B, scene, renderSettings, renderProfile);
      const zoneLookup = new Map<HotZoneCategory, HotZone[]>();
      const hoverHalo = createHoverHalo(B, scene);
      const eventCardRoot = infoCardModel || eventPanelHtmlRef?.current
        ? createEventCardTemplate(B, scene, infoCardModel)
        : null;
      let eventCardHtmlMesh: import("@babylonjs/addons").HtmlMesh | null = null;
      let htmlMeshRenderer: import("@babylonjs/addons").HtmlMeshRenderer | null = null;

      if (addons && eventCardRoot && eventPanelHtmlRef?.current) {
        // Babylon's in-scene HtmlMesh path writes a depth mask, so WebGL geometry
        // correctly occludes the DOM video. Keep the renderer's overlay layer enabled:
        // the installed addon uses it while sizing the shared CSS render layers.
        htmlMeshRenderer = new addons.HtmlMeshRenderer(scene, {
          parentContainerId: htmlMeshContainerId,
        });
        eventCardHtmlMesh = new addons.HtmlMesh(scene, "youtube-plane", {
          captureOnPointerEnter: true,
          fitStrategy: addons.FitStrategy.CONTAIN,
        });
        eventPanelHtmlRef.current.style.visibility = "visible";
        eventCardHtmlMesh.setContent(
          eventPanelHtmlRef.current,
          eventPanelHtmlSize.width,
          eventPanelHtmlSize.height,
        );
        eventCardHtmlMesh.position.set(0, 0, -0.035);
        eventCardHtmlMesh.parent = eventCardRoot;
        eventCardHtmlMesh.isPickable = true;
        eventCardHtmlMesh.setEnabled(false);
      }

      scene.onPointerObservable.add((pointerInfo) => {
        const pickInfo = pointerInfo.type === B.PointerEventTypes.POINTERMOVE
          ? scene.pick(scene.pointerX, scene.pointerY, (mesh) => CATEGORY_ORDER.some((category) => markerMeshes.get(category) === mesh))
          : pointerInfo.pickInfo;
        const zone = getPickedZone(pickInfo, markerMeshes, zoneLookup);
        if (pointerInfo.type === B.PointerEventTypes.POINTERMOVE) {
          if (zone?.zone_id === hoveredZoneRef.current?.zone_id) return;
          if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
          if (!zone) {
            focusHoveredZone(null);
            return;
          }
          hoverTimerRef.current = setTimeout(() => focusHoveredZone(zone), hoverDelayMs);
          return;
        }
        if (pointerInfo.type === B.PointerEventTypes.POINTERPICK) {
          const pointerEvent = pointerInfo.event as PointerEvent;
          if (pointerEvent.button === 0 && zone) selectZone(zone);
        }
      });

      const resizeObserver = new ResizeObserver(() => engine.resize());
      resizeObserver.observe(containerRef.current);

      let lastMarkerUpdate = 0;
      const beforeRenderObserver = scene.onBeforeRenderObservable.add(() => {
        earth.rotation.y += GLOBE_VIEW_CONFIG.globe.rotationSpeed;
        atmosphere.rotation.y += GLOBE_VIEW_CONFIG.globe.atmosphereRotationSpeed;
        clampCameraInsideGlobe(B, camera, cameraLowerRadiusLimitRef.current);
        emitCameraState(camera, lastCameraStateEmissionRef, onCameraStateChangeRef.current);

        const now = performance.now();
        if (frameRef.current && now - lastMarkerUpdate > 70) {
          renderHotZoneInstances(B, markerMeshes, frameRef.current.zones, now / 1000, selectedZoneIdRef.current, zoneLookup, markerStyle, renderSettings);
          lastMarkerUpdate = now;
        }
        updateHoverHalo(
          B,
          scene,
          camera,
          engine,
          hoverHalo,
          hoveredZoneRef.current,
          hoverLabelRef.current,
          now / 1000,
          zoneLookup,
          selectedZoneIdRef.current,
          markerStyle,
          renderSettings,
        );
        animateEventCard(eventCardRoot, cardAnimationStartedAtRef.current, now);
      });

      engine.runRenderLoop(() => scene.render());
      sceneRef.current = {
        engine,
        scene,
        camera,
        earth,
        markerMeshes,
        markerStyle,
        renderSettings,
        zoneLookup,
        countryBoundaryMeshes: objBoundaryMeshes,
        outlineMeshes,
        usesObjModel: Boolean(objModel),
        objModel,
        hoverHalo,
        eventCardRoot,
        eventCardHtmlMesh,
        htmlMeshRenderer,
        resizeObserver,
        beforeRenderObserver,
      };
      initializingEngine = null;

      if (frameRef.current) renderHotZoneInstances(B, markerMeshes, frameRef.current.zones, 0, selectedZoneIdRef.current, zoneLookup, markerStyle, renderSettings);
      setReady(true);
    }

    createScene().catch((error: unknown) => {
      initializingEngine?.dispose();
      initializingEngine = null;
      if (cancelled) return;
      setRenderError(error instanceof Error ? error.message : "Unable to initialize WebGL");
    });

    return () => {
      cancelled = true;
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      initializingEngine?.dispose();
      const refs = sceneRef.current;
      if (!refs) return;
      refs.resizeObserver.disconnect();
      if (refs.beforeRenderObserver) refs.scene.onBeforeRenderObservable.remove(refs.beforeRenderObserver);
      refs.htmlMeshRenderer?.dispose();
      refs.scene.dispose();
      refs.engine.dispose();
      sceneRef.current = null;
    };
  }, [eventPanelHtmlRef, eventPanelHtmlSize, focusHoveredZone, hoverDelayMs, htmlMeshContainerId, infoCardModelSourceUrl, markerStyle, modelSourceUrl, renderAttempt, renderSettings, selectZone, showProceduralLandShapes]);

  useEffect(() => {
    const camera = sceneRef.current?.camera;
    if (!camera) return;
    camera.fov = clamp(cameraFov, 0.35, 1.4);
    camera.lowerRadiusLimit = clamp(cameraLowerRadiusLimit, 0.15, 2);
  }, [cameraFov, cameraLowerRadiusLimit]);

  useEffect(() => {
    const refs = sceneRef.current;
    if (!refs || !ready || !boundarySourceUrl || refs.usesObjModel) return;

    let cancelled = false;

    void import("@babylonjs/core")
      .then((B) => createCountryBoundaryMeshes(B, refs.scene, boundarySourceUrl, refs.renderSettings))
      .then((meshes) => {
        refs.countryBoundaryMeshes.forEach((mesh) => mesh.dispose());

        if (cancelled) {
          meshes.forEach((mesh) => mesh.dispose());
          return;
        }

        refs.countryBoundaryMeshes = meshes;
      })
      .catch(() => {
        refs.countryBoundaryMeshes.forEach((mesh) => mesh.dispose());
        refs.countryBoundaryMeshes = [];
      });

    return () => {
      cancelled = true;
    };
  }, [boundarySourceUrl, ready]);

  useEffect(() => {
    const refs = sceneRef.current;
    if (!refs) return;

    void import("@babylonjs/core").then((B) => {
      applyEarthMaterial(B, refs.scene, refs.earth, mapMode, showProceduralLandShapes, refs.renderSettings);
      refs.outlineMeshes.forEach((mesh) => mesh.dispose());
      refs.outlineMeshes = refs.usesObjModel
        ? createObjGridMeshes(B, refs.scene, refs.objModel!, mapMode, refs.renderSettings)
        : createOutlineMeshes(B, refs.scene, mapMode, refs.renderSettings);
    });
  }, [mapMode, modelSourceUrl, showProceduralLandShapes]);

  useEffect(() => {
    const refs = sceneRef.current;
    if (!refs || !frame) return;

    void import("@babylonjs/core").then((B) => {
      renderHotZoneInstances(B, refs.markerMeshes, frame.zones, performance.now() / 1000, selectedZoneId, refs.zoneLookup, refs.markerStyle, refs.renderSettings);
    });
  }, [frame, selectedZoneId]);

  useEffect(() => {
    const refs = sceneRef.current;
    if (!refs) return;
    if (!selectedZone) {
      refs.eventCardRoot?.setEnabled(false);
      refs.eventCardHtmlMesh?.setEnabled(false);
      return;
    }
    if (refs.eventCardRoot) {
      positionEventCardTemplate(refs.eventCardRoot, selectedZone);
      refs.eventCardHtmlMesh?.setEnabled(true);
      cardAnimationStartedAtRef.current = performance.now();
    }
    if (lastFlightZoneIdRef.current === selectedZone.zone_id) return;

    lastFlightZoneIdRef.current = selectedZone.zone_id;
    void import("@babylonjs/core").then((B) => {
      flyCameraToZone(B, refs.scene, refs.camera, selectedZone);
      renderHotZoneInstances(B, refs.markerMeshes, frameRef.current?.zones ?? [], performance.now() / 1000, selectedZone.zone_id, refs.zoneLookup, refs.markerStyle, refs.renderSettings);
    });
  }, [ready, selectedZone]);

  return (
    <section
      ref={containerRef}
      id={htmlMeshContainerId}
      className={cn(
        "isolate relative z-0 min-h-[560px] overflow-hidden rounded-md border border-cyan-300/15 bg-[#02070b]",
        className,
      )}
    >
      <canvas ref={canvasRef} className="h-full min-h-[560px] w-full touch-none outline-none" />

      {eventPanelHtmlContent}

      <div
        ref={hoverLabelRef}
        className={cn(
          "pointer-events-none absolute left-0 top-0 z-30 -translate-x-1/2 -translate-y-[calc(100%+18px)] text-xs font-bold text-red-100 drop-shadow-[0_0_8px_#ff1838]",
          hoveredWord ? "block" : "hidden",
        )}
      >
        {hoveredWord}
      </div>

      {eventOverlay ? (
        <div className="pointer-events-auto absolute inset-x-3 top-20 z-30 mx-auto max-w-xl lg:inset-x-auto lg:right-5 lg:top-20 lg:w-[min(440px,calc(100%-2.5rem))]">
          {eventOverlay}
        </div>
      ) : null}

      {!ready ? (
        <div className="absolute inset-0 grid place-items-center bg-black/40 text-sm text-cyan-100">
          {renderError ? (
            <div className="mx-6 max-w-sm border border-red-400/30 bg-black/75 p-4 text-center">
              <p className="font-medium text-red-100">3D globe unavailable</p>
              <p className="mt-1 text-xs text-red-100/70">{renderError}</p>
              <button
                type="button"
                onClick={() => setRenderAttempt((attempt) => attempt + 1)}
                className="mt-3 border border-cyan-300/35 px-3 py-1.5 text-xs text-cyan-100"
              >
                Retry WebGL
              </button>
            </div>
          ) : "Initializing globe"}
        </div>
      ) : null}

      <div className="pointer-events-none absolute left-4 top-4 grid gap-2">
        <div className="rounded-md border border-cyan-300/15 bg-black/55 px-3 py-2 backdrop-blur">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-cyan-100/80">
            <RadioTower className="h-3.5 w-3.5" aria-hidden />
            {fallbackSource.mode}
          </div>
          <p className="mt-1 text-sm text-foreground">
            {status === "open" ? "Live frame linked" : status === "connecting" ? "Connecting stream" : status}
          </p>
          {frame ? (
            <p className="text-xs text-muted-foreground">
              {frame.zones.length} zones · intensity {frame.global_intensity}
            </p>
          ) : null}
        </div>
        {error ? (
          <div className="flex max-w-xs items-center gap-2 rounded-md border border-red-400/25 bg-red-950/60 px-3 py-2 text-xs text-red-100">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
            {error}
          </div>
        ) : null}
      </div>

      <div className="absolute bottom-4 left-4 right-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="pointer-events-none hidden items-end gap-2 lg:flex">
          {CATEGORY_ORDER.map((category) => (
            <div key={category} className="rounded-sm border border-white/10 bg-black/45 px-2.5 py-2 backdrop-blur">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span
                  className="h-2.5 w-2.5 rounded-full shadow-[0_0_16px_currentColor]"
                  style={{ color: categoryStyle[category].glow, backgroundColor: categoryStyle[category].glow }}
                />
                {categoryStyle[category].label}
              </div>
            </div>
          ))}
        </div>

        {showTelemetryOverlay && selectedZone ? (
          <ZoneTelemetryCard
            frame={frame}
            zone={selectedZone}
            onSelectZone={(zoneId) => {
              const zone = frame?.zones.find((candidate) => candidate.zone_id === zoneId);
              if (zone) selectZone(zone);
            }}
          />
        ) : null}
      </div>
    </section>
  );
}

function ZoneTelemetryCard({
  frame,
  zone,
  onSelectZone,
}: {
  frame: GlobeFrame | null;
  zone: HotZone;
  onSelectZone: (zoneId: string) => void;
}) {
  const style = HOT_ZONE_CATEGORY_STYLE[zone.category];
  const topZones = frame?.zones.slice().sort((a, b) => b.intensity - a.intensity).slice(0, 4) ?? [];

  return (
    <aside className="rounded-md border border-white/10 bg-black/62 p-3 shadow-2xl backdrop-blur">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
            <Activity className="h-3.5 w-3.5" aria-hidden />
            Focus zone
          </div>
          <h2 className="mt-1 text-sm font-semibold">{style.label}</h2>
        </div>
        <span className="rounded-sm px-2 py-1 text-xs text-black" style={{ backgroundColor: style.glow }}>
          {zone.trend}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <Metric label="Intensity" value={zone.intensity} />
        <Metric label="Events" value={zone.event_count} />
        <Metric label="Mentions" value={zone.mention_count} />
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {zone.top_themes.map((theme) => (
          <span key={theme} className="rounded-sm bg-white/10 px-2 py-1 text-xs text-muted-foreground">
            {theme}
          </span>
        ))}
      </div>

      {topZones.length ? (
        <div className="mt-3 grid gap-1.5">
          {topZones.map((candidate) => (
            <button
              key={candidate.zone_id}
              type="button"
              onClick={() => onSelectZone(candidate.zone_id)}
              className={cn(
                "flex items-center justify-between rounded-sm border px-2 py-1.5 text-left text-xs transition hover:bg-white/10",
                candidate.zone_id === zone.zone_id ? "border-cyan-300/50 bg-cyan-300/10" : "border-white/10",
              )}
            >
              <span className="truncate">{candidate.zone_id}</span>
              <span>{candidate.intensity}</span>
            </button>
          ))}
        </div>
      ) : null}
    </aside>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-sm border border-white/10 bg-white/[0.04] px-2 py-2">
      <p className="text-base font-semibold">{value}</p>
      <p className="text-[0.68rem] uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
    </div>
  );
}

function getPickedZone(
  pickInfo: import("@babylonjs/core").Nullable<import("@babylonjs/core").PickingInfo> | undefined,
  markerMeshes: Map<HotZoneCategory, import("@babylonjs/core").Mesh>,
  zoneLookup: Map<HotZoneCategory, HotZone[]>,
) {
  const pickedMesh = pickInfo?.pickedMesh;
  const instanceIndex = pickInfo?.thinInstanceIndex;
  if (!pickedMesh || instanceIndex === undefined || instanceIndex === null || instanceIndex < 0) return null;
  const category = CATEGORY_ORDER.find((candidate) => markerMeshes.get(candidate) === pickedMesh);
  return category ? zoneLookup.get(category)?.[instanceIndex] ?? null : null;
}

function createHoverHalo(B: BabylonModule, scene: import("@babylonjs/core").Scene) {
  const halo = B.MeshBuilder.CreateTorus("event-hover-halo", { diameter: 2, thickness: 0.12, tessellation: 64 }, scene);
  const material = new B.StandardMaterial("event-hover-halo-material", scene);
  material.emissiveColor = new B.Color3(1, 0.015, 0.08);
  material.diffuseColor = new B.Color3(0.35, 0, 0.02);
  material.disableLighting = true;
  material.alpha = 0.96;
  halo.material = material;
  halo.isPickable = false;
  halo.setEnabled(false);
  return halo;
}

function updateHoverHalo(
  B: BabylonModule,
  scene: import("@babylonjs/core").Scene,
  camera: import("@babylonjs/core").ArcRotateCamera,
  engine: import("@babylonjs/core").Engine,
  halo: import("@babylonjs/core").Mesh,
  zone: HotZone | null,
  label: HTMLDivElement | null,
  phase: number,
  zoneLookup: Map<HotZoneCategory, HotZone[]>,
  selectedZoneId: string | null,
  markerStyle: "pressure" | "export",
  settings: GlobeRenderSettings,
) {
  if (!zone) {
    halo.setEnabled(false);
    return;
  }
  const zoneIndex = zoneLookup.get(zone.category)?.findIndex((candidate) => candidate.zone_id === zone.zone_id) ?? -1;
  const layout = getHotZoneMarkerLayout(
    zone,
    Math.max(0, zoneIndex),
    phase,
    zone.zone_id === selectedZoneId,
    markerStyle,
    settings,
  );
  const position = new B.Vector3(layout.point.x, layout.point.y, layout.point.z);
  const normal = position.clone().normalize();
  const up = B.Axis.Y;
  const axis = B.Vector3.Cross(up, normal);
  const angle = Math.acos(clamp(B.Vector3.Dot(up, normal), -1, 1));
  halo.position.copyFrom(position);
  halo.rotationQuaternion = axis.lengthSquared() > 0.00001
    ? B.Quaternion.RotationAxis(axis.normalize(), angle)
    : B.Quaternion.Identity();
  const nodeRadius = layout.size / 2;
  halo.scaling.setAll(getEventHoverHaloScale(nodeRadius, phase));
  halo.setEnabled(true);

  if (label) {
    const viewport = camera.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight());
    const projected = B.Vector3.Project(position, B.Matrix.Identity(), scene.getTransformMatrix(), viewport);
    label.style.left = `${projected.x}px`;
    label.style.top = `${projected.y}px`;
  }
}

function getZoneSignificantWord(zone: HotZone) {
  const candidate = (zone as HotZone & { significantWord?: unknown }).significantWord;
  if (typeof candidate === "string" && candidate) return candidate;
  return HOT_ZONE_CATEGORY_STYLE[zone.category].label.split(" ")[0]?.toUpperCase() ?? zone.category;
}

function createEventCardTemplate(B: BabylonModule, scene: import("@babylonjs/core").Scene, model: ObjGlobeModel | null) {
  const root = new B.TransformNode("event-info-card-template", scene);
  model?.objects.filter((object) => object.indices.length).forEach((object) => {
    const mesh = createObjSurfaceMesh(B, scene, model, object.name, object.name);
    const material = new B.StandardMaterial(`${object.name}-material`, scene);
    const accent = object.name.includes("Accent");
    const header = object.name.includes("Header");
    material.diffuseColor = accent ? new B.Color3(0.8, 0.01, 0.04) : header ? new B.Color3(0.05, 0.12, 0.17) : new B.Color3(0.008, 0.015, 0.022);
    material.emissiveColor = accent ? new B.Color3(0.65, 0.01, 0.03) : new B.Color3(0.01, 0.045, 0.065);
    material.alpha = accent ? 0.92 : 0.86;
    material.backFaceCulling = false;
    mesh.material = material;
    mesh.isPickable = false;
    mesh.parent = root;
  });
  root.setEnabled(false);
  return root;
}

function positionEventCardTemplate(root: import("@babylonjs/core").TransformNode, zone: HotZone) {
  const point = latLonToCartesian(zone.centroid.lat, zone.centroid.lon, EARTH_RADIUS - 1.15);
  root.position.set(point.x, point.y, point.z);
  root.rotation.set(degreesToRadians(-zone.centroid.lat), degreesToRadians(zone.centroid.lon), 0);
  root.scaling.set(0.05, 0.62, 0.62);
  root.setEnabled(true);
}

function animateEventCard(root: import("@babylonjs/core").TransformNode | null, startedAt: number, now: number) {
  if (!root?.isEnabled() || !startedAt) return;
  const progress = clamp((now - startedAt) / 360, 0, 1);
  const overshoot = 1 + Math.sin(progress * Math.PI) * 0.12;
  root.scaling.x = 0.62 * (0.08 + 0.92 * easeOutCubic(progress)) * overshoot;
  root.scaling.y = 0.62 * (1.12 - 0.12 * easeOutCubic(progress));
}

function createMarkerMeshes(
  B: BabylonModule,
  scene: import("@babylonjs/core").Scene,
  settings: GlobeRenderSettings,
  renderProfile: GlobeRenderProfile,
) {
  const meshes = new Map<HotZoneCategory, import("@babylonjs/core").Mesh>();

  CATEGORY_ORDER.forEach((category) => {
    const style = settings.signals[category];
    const marker = B.MeshBuilder.CreateSphere(
      `hot-zone-${category}`,
      { diameter: 1, segments: renderProfile.markerSegments },
      scene,
    );
    marker.isVisible = true;
    marker.alwaysSelectAsActiveMesh = true;
    marker.thinInstanceEnablePicking = true;

    const material = new B.StandardMaterial(`hot-zone-${category}-material`, scene);
    material.diffuseColor = new B.Color3(...style.color);
    material.emissiveColor = new B.Color3(...style.color);
    material.alpha = 0.92;
    material.disableLighting = true;
    marker.material = material;

    meshes.set(category, marker);
  });

  return meshes;
}

function renderHotZoneInstances(
  B: BabylonModule,
  markerMeshes: Map<HotZoneCategory, import("@babylonjs/core").Mesh>,
  zones: HotZone[],
  phase: number,
  selectedZoneId?: string | null,
  zoneLookup?: Map<HotZoneCategory, HotZone[]>,
  markerStyle: "pressure" | "export" = "pressure",
  settings: GlobeRenderSettings = BASELINE_GLOBE_RENDER_SETTINGS,
) {
  CATEGORY_ORDER.forEach((category) => {
    const mesh = markerMeshes.get(category);
    if (!mesh) return;

    const categoryZones = zones.filter((zone) => zone.category === category);
    zoneLookup?.set(category, categoryZones);
    const matrices = new Float32Array(categoryZones.length * 16);
    const colors = new Float32Array(categoryZones.length * 4);
    const style = settings.signals[category];

    categoryZones.forEach((zone, index) => {
      const selected = zone.zone_id === selectedZoneId;
      const { point, size } = getHotZoneMarkerLayout(zone, index, phase, selected, markerStyle, settings);
      const matrix = B.Matrix.Compose(
        new B.Vector3(size, size, size),
        B.Quaternion.Identity(),
        new B.Vector3(point.x, point.y, point.z),
      );

      matrix.copyToArray(matrices, index * 16);

      const alpha = zone.trend === "rising" ? 0.98 : zone.trend === "stable" ? 0.78 : 0.58;
      colors.set(
        selected ? [0.9, 0.98, 1, 1] : [style.color[0], style.color[1], style.color[2], alpha],
        index * 4,
      );
    });

    mesh.thinInstanceSetBuffer("matrix", matrices, 16, true);
    mesh.thinInstanceSetBuffer("color", colors, 4, true);
    mesh.thinInstanceCount = categoryZones.length;
  });
}

function getHotZoneMarkerLayout(
  zone: HotZone,
  index: number,
  phase: number,
  selected: boolean,
  markerStyle: "pressure" | "export",
  settings: GlobeRenderSettings,
) {
  const weight = getHotZoneVisualWeight(zone);
  const pulse = 1 + Math.sin(phase * getHotZonePulseSpeed(zone) + index) * (zone.trend === "rising" ? 0.28 : 0.12);
  const size = (0.028 + weight * 0.078) * pulse * (selected ? 1.78 : 1) * settings.signalSizeMultiplier;
  const markerRadius = markerStyle === "export" ? EARTH_RADIUS - 0.7 : MARKER_RADIUS;
  return {
    point: latLonToCartesian(zone.centroid.lat, zone.centroid.lon, markerRadius),
    size,
  };
}

function flyCameraToZone(
  B: BabylonModule,
  scene: import("@babylonjs/core").Scene,
  camera: import("@babylonjs/core").ArcRotateCamera,
  zone: HotZone,
) {
  const startAlpha = camera.alpha;
  const startBeta = camera.beta;
  const startRadius = camera.radius;
  const startTarget = camera.target.clone();
  const targetPoint = latLonToCartesian(zone.centroid.lat, zone.centroid.lon, EARTH_RADIUS * GLOBE_VIEW_CONFIG.cameraFlight.targetRadiusRatio);
  const endTarget = new B.Vector3(targetPoint.x, targetPoint.y, targetPoint.z);
  const endAlpha = degreesToRadians(zone.centroid.lon) - Math.PI / 2;
  const endBeta = clamp(
    Math.PI / 2 - degreesToRadians(zone.centroid.lat),
    GLOBE_VIEW_CONFIG.cameraFlight.minBeta,
    Math.PI - GLOBE_VIEW_CONFIG.cameraFlight.maxBetaOffset,
  );
  const endRadius = GLOBE_VIEW_CONFIG.cameraFlight.radius;
  const startedAt = performance.now();
  const durationMs = GLOBE_VIEW_CONFIG.cameraFlight.durationMs;

  let observer: import("@babylonjs/core").Nullable<import("@babylonjs/core").Observer<import("@babylonjs/core").Scene>> = null;
  observer = scene.onBeforeRenderObservable.add(() => {
    const progress = clamp((performance.now() - startedAt) / durationMs, 0, 1);
    const eased = easeOutCubic(progress);

    camera.alpha = lerpAngle(startAlpha, endAlpha, eased);
    camera.beta = startBeta + (endBeta - startBeta) * eased;
    camera.radius = startRadius + (endRadius - startRadius) * eased;
    camera.target = B.Vector3.Lerp(startTarget, endTarget, eased);

    if (progress >= 1 && observer) {
      scene.onBeforeRenderObservable.remove(observer);
    }
  });
}

function easeOutCubic(value: number) {
  return 1 - Math.pow(1 - value, 3);
}

function lerpAngle(start: number, end: number, amount: number) {
  const difference = Math.atan2(Math.sin(end - start), Math.cos(end - start));
  return start + difference * amount;
}

function clampCameraInsideGlobe(B: BabylonModule, camera: import("@babylonjs/core").ArcRotateCamera, lowerRadiusLimit: number) {
  const maxTargetDistance = GLOBE_VIEW_CONFIG.camera.targetMaxDistanceFromOrigin;
  const targetDistance = camera.target.length();

  if (targetDistance > maxTargetDistance) {
    camera.target = camera.target.normalize().scale(maxTargetDistance);
  }

  const maxPositionDistance = GLOBE_VIEW_CONFIG.camera.positionMaxDistanceFromOrigin;
  const positionDistance = camera.position.length();

  if (positionDistance > maxPositionDistance) {
    const inward = B.Vector3.Normalize(camera.position).scale(maxPositionDistance);
    const targetToPosition = inward.subtract(camera.target);
    camera.radius = clamp(
      targetToPosition.length(),
      lowerRadiusLimit,
      GLOBE_VIEW_CONFIG.camera.upperRadiusLimit,
    );
  }
}


function emitCameraState(
  camera: import("@babylonjs/core").ArcRotateCamera,
  lastEmissionRef: { current: { at: number; signature: string } },
  onCameraStateChange?: (state: GlobeCameraUrlState) => void,
) {
  if (!onCameraStateChange) return;

  const now = performance.now();
  if (now - lastEmissionRef.current.at < GLOBE_VIEW_CONFIG.camera.urlUpdateIntervalMs) return;

  const state = getCameraState(camera);
  const signature = [
    state.alpha,
    state.beta,
    state.radius,
    state.positionX,
    state.positionY,
    state.positionZ,
    state.targetX,
    state.targetY,
    state.targetZ,
  ]
    .map((value) => value.toFixed(GLOBE_VIEW_CONFIG.camera.urlPrecision))
    .join(",");

  if (signature === lastEmissionRef.current.signature) return;

  lastEmissionRef.current = { at: now, signature };
  onCameraStateChange(state);
}

function getCameraState(camera: import("@babylonjs/core").ArcRotateCamera): GlobeCameraUrlState {
  return {
    alpha: camera.alpha,
    beta: camera.beta,
    radius: camera.radius,
    positionX: camera.position.x,
    positionY: camera.position.y,
    positionZ: camera.position.z,
    targetX: camera.target.x,
    targetY: camera.target.y,
    targetZ: camera.target.z,
  };
}

async function createCountryBoundaryMeshes(
  B: BabylonModule,
  scene: import("@babylonjs/core").Scene,
  boundarySourceUrl: string,
  settings: GlobeRenderSettings,
) {
  const response = await fetch(boundarySourceUrl);
  if (!response.ok) throw new Error(`Boundary source failed: ${response.status}`);

  const collection = (await response.json()) as BoundaryFeatureCollection;
  const boundaryLines = extractBoundaryLineStrings(collection)
    .flatMap((line) => splitAntimeridianLine(line))
    .filter((line) => line.length > 1);

  const haloLines = createBoundaryVectorLines(B, boundaryLines, EARTH_RADIUS + GLOBE_VIEW_CONFIG.boundaries.haloSurfaceOffset);
  const coreLines = createBoundaryVectorLines(B, boundaryLines, EARTH_RADIUS + GLOBE_VIEW_CONFIG.boundaries.coreSurfaceOffset);
  const halo = B.MeshBuilder.CreateLineSystem("country-boundaries-halo", { lines: haloLines }, scene);
  const core = B.MeshBuilder.CreateLineSystem("country-boundaries-core", { lines: coreLines }, scene);

  halo.color = new B.Color3(...settings.boundaries.haloColor);
  halo.alpha = settings.boundaries.haloAlpha;
  halo.isPickable = false;
  halo.alwaysSelectAsActiveMesh = true;

  core.color = new B.Color3(...settings.boundaries.coreColor);
  core.alpha = settings.boundaries.coreAlpha;
  core.isPickable = false;
  core.alwaysSelectAsActiveMesh = true;

  return [halo, core];
}

function loadObjGlobeModel(url: string) {
  const cached = OBJ_MODEL_CACHE.get(url);
  if (cached) return cached;
  const request = fetch(url, { cache: "force-cache" }).then(async (response) => {
    if (!response.ok) throw new Error(`OBJ globe model failed: ${response.status}`);
    return parseObjGlobeModel(await response.text());
  });
  OBJ_MODEL_CACHE.set(url, request);
  return request;
}

function createObjSurfaceMesh(
  B: BabylonModule,
  scene: import("@babylonjs/core").Scene,
  model: ObjGlobeModel,
  objectName: string,
  meshName: string,
) {
  const object = findObjObject(model, objectName);
  if (!object?.positions.length || !object.indices.length) throw new Error(`OBJ object ${objectName} is missing mesh faces`);
  const mesh = new B.Mesh(meshName, scene);
  const normals: number[] = [];
  B.VertexData.ComputeNormals(object.positions, object.indices, normals);
  const data = new B.VertexData();
  data.positions = object.positions;
  data.indices = object.indices;
  data.normals = normals;
  data.applyToMesh(mesh, false);
  return mesh;
}

function createObjBoundaryMeshes(
  B: BabylonModule,
  scene: import("@babylonjs/core").Scene,
  model: ObjGlobeModel,
  settings: GlobeRenderSettings,
) {
  const sourceLines = getObjBoundaryLines(model);
  const coreLines = objLinesToBabylon(B, sourceLines, 1);
  const haloScale = (EARTH_RADIUS + GLOBE_VIEW_CONFIG.boundaries.haloSurfaceOffset) /
    (EARTH_RADIUS + GLOBE_VIEW_CONFIG.boundaries.coreSurfaceOffset);
  const haloLines = objLinesToBabylon(B, sourceLines, haloScale);
  const halo = B.MeshBuilder.CreateLineSystem("country-boundaries-halo-obj", { lines: haloLines }, scene);
  const core = B.MeshBuilder.CreateLineSystem("country-boundaries-core-obj", { lines: coreLines }, scene);
  halo.color = new B.Color3(...settings.boundaries.haloColor);
  halo.alpha = settings.boundaries.haloAlpha;
  halo.isPickable = false;
  core.color = new B.Color3(...settings.boundaries.coreColor);
  core.alpha = settings.boundaries.coreAlpha;
  core.isPickable = false;
  return [halo, core];
}

function createObjGridMeshes(
  B: BabylonModule,
  scene: import("@babylonjs/core").Scene,
  model: ObjGlobeModel,
  mapMode: GlobeMapMode,
  settings: GlobeRenderSettings,
) {
  if (mapMode !== "outline") return [];
  return ["LatitudeGrid", "LongitudeGrid"].flatMap((name) => {
    const object = findObjObject(model, name);
    if (!object?.lines.length) return [];
    const mesh = B.MeshBuilder.CreateLineSystem(`${name}-obj`, { lines: objLinesToBabylon(B, object.lines, 1) }, scene);
    mesh.color = new B.Color3(...settings.earth.gridColor);
    mesh.alpha = name === "LatitudeGrid" ? GLOBE_VIEW_CONFIG.globe.gridAlpha.latitude : GLOBE_VIEW_CONFIG.globe.gridAlpha.longitude;
    mesh.isPickable = false;
    return [mesh];
  });
}

function objLinesToBabylon(B: BabylonModule, lines: ObjVector[][], scale: number) {
  return lines.map((line) => line.map(([x, y, z]) => new B.Vector3(x * scale, y * scale, z * scale)));
}

function createBoundaryVectorLines(
  B: BabylonModule,
  lines: BoundaryCoordinate[][],
  radius: number,
) {
  return lines.map((line) =>
    line.map(([lon, lat]) => {
      const point = latLonToCartesian(lat, lon, radius);
      return new B.Vector3(point.x, point.y, point.z);
    }),
  );
}

function applyEarthMaterial(
  B: BabylonModule,
  scene: import("@babylonjs/core").Scene,
  earth: import("@babylonjs/core").Mesh,
  mapMode: GlobeMapMode,
  showProceduralLandShapes = true,
  settings: GlobeRenderSettings = BASELINE_GLOBE_RENDER_SETTINGS,
) {
  const existingMaterial = earth.material;
  if (existingMaterial) existingMaterial.dispose();

  const material = new B.StandardMaterial(`earth-${mapMode}-material`, scene);
  material.specularColor = new B.Color3(...settings.earth.specular);
  material.ambientColor = new B.Color3(...settings.earth.ambient);
  material.backFaceCulling = false;
  material.twoSidedLighting = true;

  if (mapMode === "outline") {
    material.diffuseTexture = createOutlineTexture(B, scene, showProceduralLandShapes);
    material.diffuseColor = new B.Color3(...settings.earth.outlineDiffuse);
    material.emissiveColor = new B.Color3(...settings.earth.outlineEmissive);
    material.alpha = settings.earth.outlineAlpha;
    earth.enableEdgesRendering();
    earth.edgesWidth = 1.8;
    earth.edgesColor = new B.Color4(0.08, 0.68, 1, 0.28);
  } else {
    material.diffuseTexture = createTerrainTexture(B, scene);
    material.diffuseColor = new B.Color3(0.9, 0.95, 1);
    material.emissiveColor = new B.Color3(...settings.earth.terrainEmissive);
    material.alpha = 1;
    earth.disableEdgesRendering();
  }

  earth.material = material;
}

function createTerrainTexture(B: BabylonModule, scene: import("@babylonjs/core").Scene) {
  const texture = new B.DynamicTexture("cyber-terrain-texture", { width: 1024, height: 512 }, scene, false);
  const context = texture.getContext();
  const gradient = context.createLinearGradient(0, 0, 1024, 512);
  gradient.addColorStop(0, "#03111c");
  gradient.addColorStop(0.48, "#0a2634");
  gradient.addColorStop(1, "#04121b");
  context.fillStyle = gradient;
  context.fillRect(0, 0, 1024, 512);

  context.globalAlpha = 0.7;
  context.fillStyle = "#1f5d4d";
  drawLandMass(context, 170, 185, 130, 70, 13);
  drawLandMass(context, 300, 250, 115, 95, 31);
  drawLandMass(context, 520, 180, 160, 92, 47);
  drawLandMass(context, 635, 282, 105, 70, 61);
  drawLandMass(context, 805, 225, 175, 88, 79);

  context.globalAlpha = 0.24;
  context.strokeStyle = "#72d6ff";
  context.lineWidth = 1;
  for (let x = 0; x <= 1024; x += 64) {
    context.beginPath();
    context.moveTo(x, 0);
    context.lineTo(x, 512);
    context.stroke();
  }
  for (let y = 0; y <= 512; y += 64) {
    context.beginPath();
    context.moveTo(0, y);
    context.lineTo(1024, y);
    context.stroke();
  }

  texture.update();
  return texture;
}

function createOutlineTexture(B: BabylonModule, scene: import("@babylonjs/core").Scene, showProceduralLandShapes = true) {
  const texture = new B.DynamicTexture("outline-entity-texture", { width: 1024, height: 512 }, scene, false);
  const context = texture.getContext();
  context.fillStyle = "#020d14";
  context.fillRect(0, 0, 1024, 512);

  context.globalAlpha = 0.34;
  context.strokeStyle = "#1aa7ff";
  context.lineWidth = 1;
  for (let x = 0; x <= 1024; x += 96) {
    context.beginPath();
    context.moveTo(x, 0);
    context.lineTo(x, 512);
    context.stroke();
  }
  for (let y = 0; y <= 512; y += 96) {
    context.beginPath();
    context.moveTo(0, y);
    context.lineTo(1024, y);
    context.stroke();
  }

  if (showProceduralLandShapes) {
    context.globalAlpha = 0.96;
    context.strokeStyle = "#55d6ff";
    context.lineWidth = 2.4;
    drawLandOutline(context, 170, 185, 130, 70, 13);
    drawLandOutline(context, 300, 250, 115, 95, 31);
    drawLandOutline(context, 520, 180, 160, 92, 47);
    drawLandOutline(context, 635, 282, 105, 70, 61);
    drawLandOutline(context, 805, 225, 175, 88, 79);

    context.globalAlpha = 0.36;
    context.strokeStyle = "#b7f3ff";
    context.lineWidth = 1;
    drawLandOutline(context, 205, 170, 42, 22, 17);
    drawLandOutline(context, 575, 205, 58, 32, 53);
    drawLandOutline(context, 850, 268, 64, 28, 83);
  }

  texture.update();
  return texture;
}

function drawLandMass(
  context: {
    beginPath: () => void;
    closePath: () => void;
    fill: () => void;
    lineTo: (x: number, y: number) => void;
    moveTo: (x: number, y: number) => void;
  },
  centerX: number,
  centerY: number,
  width: number,
  height: number,
  seed: number,
) {
  context.beginPath();
  for (let step = 0; step <= 28; step += 1) {
    const angle = (step / 28) * Math.PI * 2;
    const noise = 0.72 + Math.sin(step * seed) * 0.18 + Math.cos(step * (seed + 3)) * 0.11;
    const x = centerX + Math.cos(angle) * width * noise;
    const y = centerY + Math.sin(angle) * height * noise;
    if (step === 0) context.moveTo(x, y);
    else context.lineTo(x, y);
  }
  context.closePath();
  context.fill();
}

function drawLandOutline(
  context: {
    beginPath: () => void;
    closePath: () => void;
    lineTo: (x: number, y: number) => void;
    moveTo: (x: number, y: number) => void;
    stroke: () => void;
  },
  centerX: number,
  centerY: number,
  width: number,
  height: number,
  seed: number,
) {
  context.beginPath();
  for (let step = 0; step <= 34; step += 1) {
    const angle = (step / 34) * Math.PI * 2;
    const noise = 0.72 + Math.sin(step * seed) * 0.18 + Math.cos(step * (seed + 3)) * 0.11;
    const x = centerX + Math.cos(angle) * width * noise;
    const y = centerY + Math.sin(angle) * height * noise;
    if (step === 0) context.moveTo(x, y);
    else context.lineTo(x, y);
  }
  context.closePath();
  context.stroke();
}

function createOutlineMeshes(B: BabylonModule, scene: import("@babylonjs/core").Scene, mapMode: GlobeMapMode, settings: GlobeRenderSettings) {
  const meshes: import("@babylonjs/core").LinesMesh[] = [];
  if (mapMode !== "outline") return meshes;

  for (let lat = -60; lat <= 60; lat += 30) {
    meshes.push(createLatitudeLine(B, scene, lat, settings));
  }

  for (let lon = -150; lon <= 180; lon += 30) {
    meshes.push(createLongitudeLine(B, scene, lon, settings));
  }

  return meshes;
}

function createLatitudeLine(B: BabylonModule, scene: import("@babylonjs/core").Scene, lat: number, settings: GlobeRenderSettings) {
  const points = [];
  for (let lon = -180; lon <= 180; lon += 4) {
    const point = latLonToCartesian(lat, lon, EARTH_RADIUS + GLOBE_VIEW_CONFIG.boundaries.surfaceOffset);
    points.push(new B.Vector3(point.x, point.y, point.z));
  }

  const line = B.MeshBuilder.CreateLines(`latitude-${lat}`, { points }, scene);
  line.color = new B.Color3(...settings.earth.gridColor);
  line.alpha = GLOBE_VIEW_CONFIG.globe.gridAlpha.latitude;
  return line;
}

function createLongitudeLine(B: BabylonModule, scene: import("@babylonjs/core").Scene, lon: number, settings: GlobeRenderSettings) {
  const points = [];
  for (let lat = -86; lat <= 86; lat += 4) {
    const point = latLonToCartesian(lat, lon, EARTH_RADIUS + GLOBE_VIEW_CONFIG.boundaries.surfaceOffset);
    points.push(new B.Vector3(point.x, point.y, point.z));
  }

  const line = B.MeshBuilder.CreateLines(`longitude-${lon}`, { points }, scene);
  line.color = new B.Color3(...settings.earth.gridColor);
  line.alpha = GLOBE_VIEW_CONFIG.globe.gridAlpha.longitude;
  return line;
}

function degreesToRadians(value: number) {
  return (value * Math.PI) / 180;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getBrowserGlobeRenderProfile(container: HTMLDivElement) {
  const navigatorWithMemory = navigator as Navigator & { deviceMemory?: number };
  return getGlobeRenderProfile({
    viewportWidth: container.clientWidth || window.innerWidth,
    coarsePointer: window.matchMedia("(pointer: coarse)").matches,
    deviceMemoryGb: navigatorWithMemory.deviceMemory,
  });
}
